import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import qs from 'query-string';

// Components
import Heading from '../Heading';
import Preloader from '../Preloader';
import ProductCard from '../ProductCard';
import ProductCardCompact from '../ProductCardCompact';

// State
import { allConditionsCheckedSelector } from '../../state/modules/stock/selectors';
import { removeItem } from '../../state/modules/cart/actions';
import {
  addReward,
  addToCart,
  applyStock,
  cancelRemovedIngredients,
  removeIngredient,
} from '../../state/modules/stock/actions';

// Router
import { getRoute } from '../../entry/routes';

// Styles
import styles from './StockGoods.styl';

const cx = classNames.bind(styles);

export default function StockGoods(props) {
  const { stockRole } = props;
  const isLoadingStockApply = useSelector(state => state?.stock?.isLoadingStockApply);

  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isPhone, isTablet } = useSelector(state => state.responsive);
  const allConditionsChecked = useSelector(allConditionsCheckedSelector);
  const { isApplied, data } = useSelector(state => state.stock);
  const stockGoodsRef = useRef({});

  const [isAdded, setIsAdded] = useState(false);

  const { code = '', details = {} } = data;
  const { conditions = [], rewards = [] } = details;

  const { stockCode, conditionIdx, rewardIdx } = params;

  const currentCondition = conditions[Number(conditionIdx)] || {};
  const currentReward = rewards[Number(rewardIdx)] || {};

  const isConditions = stockRole === 'conditions';
  const type = isConditions ? 'good' : 'gift';
  const goods = isConditions ? currentCondition.goods || [] : currentReward.goods || [];
  const title = isConditions ? currentCondition.criteria || '' : currentReward.message || '';
  const productIndex = isConditions ? conditionIdx : rewardIdx;
  // не выводим товары, которые есть в стоп листе
  const filtredGoods = goods.filter(
    good => good.variations.filter(variation => !variation.is_in_stop_list).length > 0,
  );

  const removeIngredientAction = ({ id, variation, ingredientId }) => {
    return dispatch(removeIngredient(id, variation.id, productIndex, ingredientId, stockRole));
  };

  const cancelRemovedIngredientsAction = ({ id, variation }) => {
    return dispatch(cancelRemovedIngredients(id, variation.id, productIndex, stockRole));
  };

  const addToCartAction = ({ goodId, variation }) => {
    const { replace, item } = qs.parse(history.location.search);

    if (isConditions) {
      if (replace && item) {
        dispatch(removeItem(item));
      }

      const composition = [{ type: 'good', item: variation }];
      return dispatch(addToCart(goodId, conditionIdx, composition)).then(() => {
        setIsAdded(true);
      });
    }

    const ingredients = variation.include_ingredients || [];

    const filtredIngredients =
      ingredients
        .filter(ingredient => ingredient.removed)
        .map(ingredient => ({ id: ingredient.id, count: 0 })) || [];

    const composition = [{ id: variation.id, ingredients: filtredIngredients }];
    return dispatch(addReward(goodId, rewardIdx, composition, replace !== undefined)).then(() => {
      setIsAdded(true);
    });
  };

  useEffect(() => {
    const offset = isPhone || isTablet ? 0 : -85;

    window.scrollBy({
      top: stockGoodsRef.current.getBoundingClientRect().top + offset,
    });

    // если в стейте применен один промокод, а в урле другой, то применяем из урла
    if (stockCode !== code) {
      dispatch(applyStock({ stock_code: stockCode }));
    }

    return () => {
      stockGoodsRef.current.scrollIntoView({
        block: 'start',
      });
    };
  }, []);

  useEffect(() => {
    if (isAdded && (isConditions ? currentCondition.check : currentReward.applied)) {
      history.goBack();
    }
  }, [isAdded, currentCondition.check, currentReward.applied]);

  // если промокод не применен, то мы не должны находиться на этой странице
  useEffect(() => {
    if (!isApplied) {
      history.push(getRoute('home'));
    }
  }, [isApplied]);

  // если это страница с призами за промокод, но условия не выполнены, то мы не должны находиться на этой странице
  useEffect(() => {
    if (stockRole === 'rewards' && !allConditionsChecked) {
      history.push(getRoute('home'));
    }
  }, [stockRole, allConditionsChecked]);

  return (
    <div className={cx('StockGoods')} ref={stockGoodsRef}>
      {goods.length > 0 ? (
        <>
          <Heading className={cx('StockGoods__heading')}>{title}</Heading>
          <div className={cx('StockGoods__list')}>
            {filtredGoods.map(good =>
              isPhone ? (
                <ProductCardCompact
                  isLoadingStockApply={isLoadingStockApply}
                  className={cx('StockGoods__item')}
                  key={good.alias}
                  good={good}
                  stockRole={stockRole}
                  removeIngredientAction={removeIngredientAction}
                  cancelRemovedIngredientsAction={cancelRemovedIngredientsAction}
                  addToCartAction={addToCartAction}
                  type={type}
                  isPromo={good.id === 429}
                />
              ) : (
                <ProductCard
                  isLoadingStockApply={isLoadingStockApply}
                  className={cx('StockGoods__item')}
                  key={good.alias}
                  good={good}
                  stockRole={stockRole}
                  removeIngredientAction={removeIngredientAction}
                  cancelRemovedIngredientsAction={cancelRemovedIngredientsAction}
                  addToCartAction={addToCartAction}
                  type={type}
                  isPromo={good.id === 429}
                />
              ),
            )}
          </div>
        </>
      ) : (
        <Preloader />
      )}
    </div>
  );
}

StockGoods.propTypes = {
  stockRole: PropTypes.oneOf(['conditions', 'rewards']).isRequired,
};
